import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VerifiedBadge from './VerifiedBadge';
import { resolveAvatarUrl } from '../api';
import { isSqrtClan, fmtLaunchedAgo, fmtBits } from '../lib/clan';
import { formatUsdCompact, formatCountCompact } from '../lib/format';

/**
 * Creator card — v3 (poster-style). Layout:
 *   Top-right badges (Sold Out / Filling Fast)
 *   Row 1: [avatar 72×72 rounded-square] · [name+verified · @handle · category · bio]
 *   Row 2: supply progress bar (bits_minted / supply_cap)
 *   Row 3: stats — market cap · holders count
 *   Row 4: price · 24h change · Trade button
 *
 * Rationale (all from user feedback):
 *  - display_name never truncates below ~15 chars because it lives on its own line.
 *  - Category badge sits BELOW handle → long labels don't push name off-card.
 *  - Bio kept as a small subline (looks intentional now that cards are bigger).
 *  - +Market cap / +Holders visible upfront so a trader can decide without clicking.
 *  - Percentage now labeled '24h' (was ambiguous).
 */
export default function CreatorCard({ creator, onTrade, highlightChange = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { display_name, handle, bio, categories, gradient, initials, clan, verified, avatar } = creator;
  const avatarUrl = resolveAvatarUrl(avatar);
  const primaryCategory = categories?.[0] || 'Creator';
  const tagColor = CATEGORY_COLOR[primaryCategory] || 'var(--clan-purple)';
  const change24 = Number(clan?.change_24h || 0);
  const up = change24 >= 0;
  const bitsMinted = Number(clan?.bits_minted || 0);
  const supplyCap = Number(clan?.supply_cap || 0);
  // sqrt clans have no supply cap — the fill %, "Bits left", and sold-out
  // concepts don't apply. Legacy `linear` clans keep the old UI.
  const isSqrt = isSqrtClan(clan);
  const soldOut = !isSqrt && !!clan?.sold_out;
  const fillPct = supplyCap > 0 && !isSqrt ? Math.min(100, (bitsMinted / supplyCap) * 100) : 0;
  const fillingFast = !isSqrt && !soldOut && fillPct >= 80;
  const launchedAgo = isSqrt ? fmtLaunchedAgo(clan?.launch_at) : null;
  const volume24h = Number(clan?.volume_24h || 0);
  const holdersCount = Number(clan?.holders_count || clan?.members_count || 0);
  const sparkPrices = Array.isArray(clan?.spark_prices) ? clan.spark_prices : [];

  // Curve steepness signal — surfaces the marginal cost of the NEXT Bit so
  // fans can eyeball how much upside remains on the sqrt bonding curve.
  // Tiers are absolute-supply based (there is no cap on sqrt clans):
  //   • < 1,000 minted  → 🔥 Early curve
  //   • 1k – 6k minted  → 📈 Rising curve
  //   • > 6,000 minted  → 🏔️ Established curve
  // The +Δ shown is (m·√(n+2) − m·√(n+1)) so it stays curve-accurate.
  const curveSignal = (() => {
    if (!isSqrt) return null;
    const m = Number(clan?.multiplier || 0);
    if (m <= 0) return null;
    const n = bitsMinted;
    const nextPrice = m * Math.sqrt(n + 2);
    const currentPriceLive = m * Math.sqrt(n + 1);
    const delta = Math.max(0, nextPrice - currentPriceLive);
    let tier;
    if (n < 1000) tier = { emoji: '🔥', label: t('card.curveEarly'), klass: 'early' };
    else if (n < 6000) tier = { emoji: '📈', label: t('card.curveRising'), klass: 'rising' };
    else tier = { emoji: '🏔️', label: t('card.curveEstablished'), klass: 'established' };
    return { ...tier, delta };
  })();

  // Per-card accent — deterministic hue derived from handle. Applied as a
  // subtle 1.5px border + tinted glow on a dark graphite surface so each card
  // is distinctly identifiable without breaking the Circuit Prism dark theme.
  const accentRGB = pickAccentColor(handle);
  const cardTint = {
    background: 'rgba(24, 28, 40, 0.72)',
    border: `1.5px solid rgba(${accentRGB}, 0.42)`,
    boxShadow: `0 10px 28px rgba(${accentRGB}, 0.14), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(150, 200, 255, 0.06)`,
  };

  // Whole-card navigation. Clicking anywhere on the card (except the Trade
  // button, which stops propagation) opens the creator profile. Keeps keyboard
  // affordance via role/tabIndex/Enter-space handlers so screen readers still
  // parse this as an actionable element.
  const openProfile = () => navigate(`/creator/${handle}`);
  const onCardKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProfile(); }
  };

  return (
    <div
      className="creator-card v3"
      style={cardTint}
      data-testid={`creator-card-${handle}`}
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={onCardKey}
      aria-label={`View ${display_name}'s profile`}
    >
      {highlightChange && (
        <span
          className={`c-trend-badge ${up ? 'up' : 'down'}`}
          data-testid={`creator-trend-badge-${handle}`}
          title={`${up ? '+' : ''}${change24.toFixed(1)}% over last 24h`}
        >
          <span aria-hidden="true">🔥</span>
          {up ? '+' : ''}{change24.toFixed(1)}%
          <span className="c-trend-badge-sub">24h</span>
        </span>
      )}
      {soldOut && (
        <span className="c-supply-badge sold" data-testid={`creator-sold-out-${handle}`}>
          {t('card.soldOut')}
        </span>
      )}
      {!soldOut && fillingFast && (
        <span
          className="c-supply-badge fast"
          data-testid={`creator-filling-fast-${handle}`}
          title={`${bitsMinted.toLocaleString()} / ${supplyCap.toLocaleString()} minted (${fillPct.toFixed(0)}%)`}
        >
          {t('card.fillingFast', { pct: fillPct.toFixed(0) })}
        </span>
      )}

      <div className="c-head-row">
        <div
          className="c-avatar-lg"
          style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}
          aria-hidden="true"
        >
          {avatarUrl ? <img src={avatarUrl} alt="" className="c-avatar-img" loading="lazy" /> : <span>{initials}</span>}
        </div>
        <div className="c-head-body">
          <div className="c-name-line">
            <span className="c-name" data-testid={`creator-name-${handle}`} title={display_name}>{display_name}</span>
            {verified && <VerifiedBadge size="sm" />}
            {/* Subtle nudge — tells first-time users that the tile itself is
                clickable (opens the creator's public profile). The Buy
                button below stops propagation, so tapping it doesn't
                trigger the profile navigation. */}
            <span
              className="c-profile-hint"
              data-testid={`creator-profile-hint-${handle}`}
              title={t('card.profileHintTitle')}
              aria-hidden="true"
            >{t('card.profileHint')}</span>
          </div>
          <div className="c-handle">@{handle}</div>
          <span className="c-category-pill" style={{ background: `${tagColor}18`, color: tagColor }} title={primaryCategory}>
            {primaryCategory}
          </span>
          {bio && <div className="c-bio" title={bio}>{bio}</div>}
        </div>
      </div>

      {/* Curve-aware supply row: sqrt clans show "Supply: N Bits" + launched-ago
          (no cap → % minted / Bits-left doesn't apply). Legacy linear clans
          keep the fill-progress bar + Waitlist/Bits-left copy. */}
      {isSqrt ? (
        <div className="c-supply-row sqrt" data-testid={`creator-supply-${handle}`}>
          <div className="c-supply-txt sqrt">
            <span className="c-supply-minted">{t('card.supplyLabel')} <b>{fmtBits(bitsMinted)}</b> {bitsMinted === 1 ? t('common.bit') : t('common.bits')}</span>
            {launchedAgo && <span className="c-supply-remaining">{launchedAgo}</span>}
          </div>
        </div>
      ) : supplyCap > 0 && (
        <div className="c-supply-row" data-testid={`creator-supply-${handle}`} title={`${bitsMinted.toLocaleString()} of ${supplyCap.toLocaleString()} Bits minted`}>
          <div className="c-supply-bar">
            <div
              className={`c-supply-fill ${soldOut ? 'sold' : fillingFast ? 'fast' : ''}`}
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <div className="c-supply-txt">
            <span>{fillPct.toFixed(0)}% minted</span>
            <span className="c-supply-remaining">
              {soldOut ? t('card.waitlist') : t('card.bitsLeft', { count: (supplyCap - bitsMinted).toLocaleString() })}
            </span>
          </div>
        </div>
      )}

      {sparkPrices.length >= 2 && (
        <MiniSparkline
          prices={sparkPrices}
          up={up}
          data-testid={`creator-spark-${handle}`}
        />
      )}

      <div className="c-stats-row">
        <div className="c-stat" title={t('card.vol24hTitle')}>
          <span className="c-stat-label">{t('card.vol24h')}</span>
          <span className="c-stat-val" data-testid={`creator-vol24h-${handle}`}>{formatUsdCompact(volume24h)}</span>
        </div>
        <div className="c-stat" title={t('card.holdersTitle')}>
          <span className="c-stat-label">{t('card.holders')}</span>
          <span className="c-stat-val" data-testid={`creator-holders-${handle}`}>{formatCountCompact(holdersCount)}</span>
        </div>
      </div>

      {curveSignal && (
        <div
          className={`c-curve-signal ${curveSignal.klass}`}
          data-testid={`creator-curve-signal-${handle}`}
          title={t('card.curveTooltip', { delta: curveSignal.delta.toFixed(2) })}
        >
          <span className="c-curve-emoji" aria-hidden="true">{curveSignal.emoji}</span>
          <span className="c-curve-label">{curveSignal.label}</span>
          <span className="c-curve-sep">·</span>
          <span className="c-curve-delta">{t('card.curveDelta', { delta: curveSignal.delta.toFixed(2) })}</span>
        </div>
      )}

      <div className="c-trade-row">
        <div className="c-price-block">
          <div className="c-price-label">{t('card.priceLabel')}</div>
          <div className="c-price" data-testid={`creator-price-${handle}`}>{Number(clan?.current_price || 0).toFixed(2)} <span>{t('common.pts')}</span></div>
          <div className={`c-change ${up ? 'up' : 'down'}`} data-testid={`creator-change-${handle}`}>
            <svg viewBox="0 0 12 12" fill="none">
              {up
                ? <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                : <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
            </svg>
            {up ? '+' : ''}{change24.toFixed(1)}% <span className="c-change-window">· 24h</span>
          </div>
        </div>
        <button
          className="c-trade-btn"
          onClick={(e) => { e.stopPropagation(); onTrade && onTrade(creator); }}
          data-testid={`trade-btn-${handle}`}
        >
          {t('card.buyBtn')}
        </button>
      </div>
    </div>
  );
}

// Category → dot color (matches Claude's home-feed palette).
const CATEGORY_COLOR = {
  'Trader': 'var(--clan-orange)',
  'Content Creator': 'var(--clan-pink)',
  'Coach': 'var(--clan-blue)',
  'Educator': 'var(--clan-green)',
  'Community Builder': 'var(--clan-purple)',
  'Influencer': 'var(--clan-yellow)',
};

/** Convert `#RRGGBB` / `#RGB` (and CSS `var(...)` fallback) to `rgba(..., a)`. */
function hexAlpha(hexOrVar, alpha) {
  if (!hexOrVar || typeof hexOrVar !== 'string') return `rgba(0, 180, 255, ${alpha})`;
  if (hexOrVar.startsWith('var(')) return `rgba(0, 180, 255, ${alpha})`;
  let h = hexOrVar.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return `rgba(0, 180, 255, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Deterministic per-creator accent palette. Applied as a saturated border
 *  + tinted glow while the card body stays uniformly graphite — giving clear
 *  card-to-card distinction on the dark Circuit Prism theme. 8 accents drawn
 *  from the app palette + close analogues (no pinks/roses that clash with
 *  magenta), hashed by handle so each creator's color is stable. */
const ACCENT_PALETTE = [
  '0, 180, 255',    // Electric blue (--clan-blue)
  '255, 87, 34',    // Hot orange (--clan-orange)
  '180, 255, 0',    // Acid lime (--clan-green)
  '224, 64, 251',   // Magenta (--clan-purple)
  '255, 193, 7',    // Amber (--clan-yellow)
  '181, 132, 245',  // Soft violet
  '13, 148, 136',   // Teal
  '99, 179, 237',   // Sky blue
];
function pickAccentColor(handle) {
  const key = String(handle || '');
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = ((hash << 5) - hash) + key.charCodeAt(i);
  const idx = Math.abs(hash) % ACCENT_PALETTE.length;
  return ACCENT_PALETTE[idx];
}

/** Legacy tint-pair picker (unused after v4 palette swap, kept for any callers). */
function pickTintPair(handle) {
  const rgb = pickAccentColor(handle);
  return [`rgba(${rgb}, 0.14)`, `rgba(${rgb}, 0.08)`];
}

/** Tiny inline sparkline of the last N trade prices. Green when the series
 *  is up over its window, red when down. Rendered as a viewBox-normalized
 *  polyline so it scales to the card width. */
function MiniSparkline({ prices, up, 'data-testid': testId }) {
  if (!Array.isArray(prices) || prices.length < 2) return null;
  const W = 100, H = 22;
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const span = (maxP - minP) || 1;
  const points = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * W;
    const y = H - ((p - minP) / span) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const stroke = up ? '#B4FF00' : '#FF5722';
  return (
    <svg
      className="c-spark-svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid={testId || 'creator-spark'}
    >
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
